import { z } from 'zod';
/**
 * Core geometric types
 */
export const PointSchema = z.object({
    x: z.number(),
    y: z.number(),
});
export const BoundsSchema = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
});
/**
 * Map layer for scene composition
 */
export const MapLayerSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    imageUrl: z.string().url(),
    visible: z.boolean().default(true),
    opacity: z.number().min(0).max(1).default(1),
    order: z.number().int().default(0), // Higher = on top
});
/**
 * Fog of War configuration
 */
export const FogOfWarSchema = z.object({
    enabled: z.boolean().default(false),
    revealedAreas: z.array(BoundsSchema).default([]),
    style: z.enum(['black', 'blur', 'dim']).default('black'),
});
/**
 * Scene represents a single map/encounter
 */
export const SceneSchema = z.object({
    id: z.string().uuid(),
    campaignId: z.string().uuid(),
    name: z.string(),
    description: z.string().optional(),
    // Grid configuration
    gridType: z.enum(['square', 'hex', 'none']).default('square'),
    gridSize: z.number().positive().default(50), // Pixels per grid square
    gridWidth: z.number().positive().default(20), // Grid squares
    gridHeight: z.number().positive().default(20), // Grid squares
    gridColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#000000'),
    gridOpacity: z.number().min(0).max(1).default(0.3),
    // Background
    backgroundColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/).default('#1a1a1a'),
    // Layers and fog
    layers: z.array(MapLayerSchema).default([]),
    fogOfWar: FogOfWarSchema.default({}),
    // Active state
    active: z.boolean().default(false), // Is this the current scene?
    // Metadata
    metadata: z.record(z.unknown()).default({}),
    createdAt: z.date(),
    updatedAt: z.date(),
});
/**
 * Ruleset defines game system rules
 */
export const RulesetSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    system: z.enum(['dnd5e', 'pf2e', 'custom']).default('dnd5e'),
    version: z.string().default('1.0.0'),
    // Dice configuration
    diceTypes: z.array(z.number()).default([4, 6, 8, 10, 12, 20, 100]),
    // Rules data (can be extended)
    rules: z.record(z.unknown()).default({}),
    metadata: z.record(z.unknown()).default({}),
    createdAt: z.date(),
    updatedAt: z.date(),
});
/**
 * Campaign is the top-level container
 */
export const CampaignSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().optional(),
    // Owner and permissions
    ownerId: z.string(), // User ID of DM
    playerIds: z.array(z.string()).default([]), // User IDs of players
    // Active scene
    activeSceneId: z.string().uuid().optional(),
    // Rules
    rulesetId: z.string().uuid(),
    // Settings
    settings: z.object({
        allowPlayerTokenControl: z.boolean().default(true),
        allowPlayerFogReveal: z.boolean().default(false),
        allowPlayerDiceRolls: z.boolean().default(true),
        enableAICopilot: z.boolean().default(true),
    }).default({}),
    // Metadata
    metadata: z.record(z.unknown()).default({}),
    createdAt: z.date(),
    updatedAt: z.date(),
});
//# sourceMappingURL=types.js.map